import { Drawer, message } from 'antd';
import ClientsSelect from 'components/ClientsSelect';
import { PaymentInfoTables } from 'components/PaymentsInfoTables';
import { FETCH_ERROR } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { useCallback, useEffect, useState } from 'react';
import { PaymentsService } from 'services/PaymentsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { BccPaymentsTable } from './BccPaymentsTable';

const PAGE_TITLE = 'Отправленные в БЦК';

function BccPayments() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const tribeId = useTribe();
  const initialPaymentsInfo = {
    cardPayments: null,
    clientsPayments: null,
    bccPayments: null,
  };
  const [paymentsInfo, setPaymentsInfo] = useState(initialPaymentsInfo);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    status: null,
    type: null,
    period: null,
  });
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const {
    state: { drawerVisible, activeRecord },
    openDrawer,
    closeDrawer,
  } = useDrawers();

  const fetchPayments = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await PaymentsService.getBccPayments(
        page,
        filters,
        tribeId,
        perPage
      );

      setPayments(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    if (activeRecord) {
      fetchPaymentsInfo();
    }
  }, [activeRecord]);

  const fetchPaymentsInfo = async () => {
    setLoading(true);

    try {
      const { card_payment, payment, bcc } =
        await PaymentsService.getBccPaymentsDetail(activeRecord?.id);

      setPaymentsInfo({
        cardPayments: card_payment,
        clientsPayments: payment,
        bccPayments: bcc,
      });
    } catch (err) {
      setPaymentsInfo(initialPaymentsInfo);
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />
      </ActionsWrapper>

      <Drawer
        title={activeRecord?.company_name}
        width="70%"
        onClose={closeDrawer}
        visible={drawerVisible}
        getContainer={false}
      >
        <PaymentInfoTables loading={loading} payments={paymentsInfo} />
      </Drawer>

      <BccPaymentsTable
        items={payments}
        current={page}
        pageSize={perPage}
        loading={loading}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />
    </>
  );
}

export { BccPayments };
